import { type FC, type ReactNode } from "react";
import DashboardKpiCard from "../DashboardKpiCard";
import { type DefinitionTooltipInfo } from "../DefinitionTooltip";

export interface DashboardMetricTileProps {
  /** Uppercase label (e.g. REVENUE, ORDERS). */
  label: string;
  /** Primary metric; strings get thousands separators when applicable. */
  value: ReactNode;
  /** Decorative metric icon (e.g. Lucide icon). */
  icon: ReactNode;
  /** Optional help content (question mark + popover, Image 02 style). */
  tooltip?: DefinitionTooltipInfo;
  tooltipAlign?: "start" | "end";
  formulaTitle?: string;
  /** Percentage point change; sign controls arrow, while trendPolarity can invert the color. */
  changePercent?: number | null;
  /** Pre-formatted change string from the API (already localized); takes precedence over `changePercent`. */
  changePercentFormattedValue?: string;
  /** Controls whether an increase or decrease is treated as the positive business outcome. */
  trendPolarity?: "higher-is-better" | "lower-is-better";
  disableChangeFallback?: boolean;
  changePercentTreatZeroAsMissing?: boolean;
  changePercentClampAbsMax?: number;
  changePercentFallbackSeed?: string | number;
  /** Text after the change, e.g. "vs last month". */
  comparisonLabel?: string;
  trendOutcomeLabel?: string;
  noTrendLabel?: string;
  noTrendTooltip?: string;
  /** When true, omit the "Trend unavailable" fallback entirely instead of showing a placeholder. */
  hideNoTrendFallback?: boolean;
  /** Muted line under the value (e.g. "Difference in monthly values"). */
  sublabel?: ReactNode;
  /** Neutral footer when no percentage is shown (e.g. inventory status). */
  statusHint?: ReactNode;
  className?: string;
}

const DashboardMetricTile: FC<DashboardMetricTileProps> = ({
  label,
  value,
  icon,
  tooltip,
  tooltipAlign = "start",
  formulaTitle = "Formula",
  changePercent,
  changePercentFormattedValue,
  trendPolarity,
  disableChangeFallback,
  changePercentTreatZeroAsMissing,
  changePercentClampAbsMax,
  changePercentFallbackSeed,
  comparisonLabel = "vs last period",
  trendOutcomeLabel,
  noTrendLabel,
  noTrendTooltip,
  hideNoTrendFallback,
  sublabel,
  statusHint,
  className = "",
}) => {
  const hasTrend = typeof changePercent === "number" && Number.isFinite(changePercent);

  return (
    <DashboardKpiCard
      className={`dashboard-metric-tile ${className}`.trim()}
      title={label}
      value={value}
      icon={icon}
      tooltip={tooltip}
      tooltipAlign={tooltipAlign}
      formulaTitle={formulaTitle}
      changePercent={changePercent}
      changePercentFormattedValue={changePercentFormattedValue}
      trendPolarity={trendPolarity}
      disableChangeFallback={disableChangeFallback}
      changePercentTreatZeroAsMissing={changePercentTreatZeroAsMissing}
      changePercentClampAbsMax={changePercentClampAbsMax}
      changePercentFallbackSeed={changePercentFallbackSeed}
      comparisonLabel={comparisonLabel}
      trendOutcomeLabel={trendOutcomeLabel}
      noTrendLabel={noTrendLabel}
      noTrendTooltip={noTrendTooltip}
      hideNoTrendFallback={hideNoTrendFallback}
      sublabel={sublabel}
      footer={!hasTrend && statusHint ? statusHint : undefined}
    />
  );
};

export default DashboardMetricTile;
